import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import type {
  CreateTransactionRequest,
  PaginationMeta,
  TransactionListQuery,
  TransactionResponse,
  UpdateTransactionRequest,
} from '@cosvision/shared';
import {
  createTransaction,
  deleteTransaction,
  listTransactions,
  updateTransaction,
} from '../lib/api/transactions.js';
import { qk } from '../lib/query-keys.js';

export type TransactionPage = { data: TransactionResponse[]; meta: PaginationMeta };

export function useTransactionsQuery(
  query: Partial<TransactionListQuery> = {},
  opts: { enabled?: boolean } = {},
): UseQueryResult<TransactionPage> {
  return useQuery({
    queryKey: qk.transactions.list(query as Record<string, unknown>),
    queryFn: () => listTransactions(query),
    enabled: opts.enabled ?? true,
  });
}

/**
 * Reuse pattern: every transaction mutation invalidates the entire
 * transactions cache plus the funds list (a new transaction locks the
 * opening balance on its fund so the `openingBalanceLocked` flag changes).
 */
function useInvalidateTransactions(): () => Promise<void> {
  const qc = useQueryClient();
  return async () => {
    await Promise.all([
      qc.invalidateQueries({ queryKey: qk.transactions.all }),
      qc.invalidateQueries({ queryKey: qk.funds.all }),
    ]);
  };
}

export function useCreateTransactionMutation(): UseMutationResult<
  TransactionResponse,
  Error,
  CreateTransactionRequest
> {
  const invalidate = useInvalidateTransactions();
  return useMutation({
    mutationFn: (input) => createTransaction(input),
    onSuccess: () => invalidate(),
  });
}

export function useUpdateTransactionMutation(): UseMutationResult<
  TransactionResponse,
  Error,
  { id: string; input: UpdateTransactionRequest }
> {
  const invalidate = useInvalidateTransactions();
  return useMutation({
    mutationFn: ({ id, input }) => updateTransaction(id, input),
    onSuccess: () => invalidate(),
  });
}

export function useDeleteTransactionMutation(): UseMutationResult<void, Error, string> {
  const invalidate = useInvalidateTransactions();
  return useMutation({
    mutationFn: (id) => deleteTransaction(id),
    onSuccess: () => invalidate(),
  });
}
